import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminPage } from './useAdminPage';
import AdminPageDeleteUserModal from './AdminPageDeleteUserModal';
import AdminPageDeleteConfigModal from './AdminPageDeleteConfigModal';
import AdminPageAddCreditsModal from './AdminPageAddCreditsModal';
import AdminPageInboundConfigModal from './AdminPageInboundConfigModal';
import AdminTabNav from './AdminTabNav';
import AdminPagination from './AdminPagination';
import { TAB_GLOBAL, TAB_PRICING, TAB_PHONE_POOL, TAB_PRIVACY_TERMS, TAB_GA_ANALYTICS } from './AdminTabNav';
import AdminGlobalSettingsPage from '../AdminGlobalSettingsPage/AdminGlobalSettingsPage';
import AdminPricingSettingsPage from '../AdminPricingSettingsPage/AdminPricingSettingsPage';
import AdminPhonePoolPage from '../AdminPhonePoolPage/AdminPhonePoolPage';
import AdminPrivacyTermsPage from '../AdminPrivacyTermsPage';
import AdminGAAnalyticsPage from '../AdminGAAnalyticsPage';
import './AdminPage.css';
import './AdminPageModals.css';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'users';
  const {
    filteredUsers,
    paginatedUsers,
    currentPage,
    setCurrentPage,
    totalPages,
    itemsPerPage,
    loading,
    error,
    successMessage,
    setSuccessMessage,
    deletionStats,
    setDeletionStats,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    deleteTarget,
    deleteConfirmValue,
    setDeleteConfirmValue,
    deleteLoading,
    deleteConfigTarget,
    deleteConfigLoading,
    inboundConfigModal,
    inboundConfig,
    loadingInboundConfig,
    inboundEditForm,
    setInboundEditForm,
    savingInboundConfig,
    addCreditsTarget,
    addCreditsAmount,
    setAddCreditsAmount,
    addCreditsDescription,
    setAddCreditsDescription,
    addCreditsLoading,
    users,
    loadUsers,
    closeDeleteConfigModal,
    closeInboundConfigModal,
    closeAddCreditsModal,
    handleDeleteConfig,
    handleSaveInboundConfig,
    handleAddCredits,
    openDeleteModal,
    closeDeleteModal,
    handleConfirmDeleteUser,
    canConfirmDeletion,
    formatStatLabel,
    getStatusColor,
    getStatusText,
  } = useAdminPage();

  if (loading) {
    return (
      <div className="admin-page-loading">
        <div className="admin-page-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-bg">
        <div className="admin-page-bg-1" />
        <div className="admin-page-bg-2" />
      </div>
      <div className="admin-page-inner">
        <div className="admin-page-header">
          <div className="admin-page-header-top">
            <div className="admin-page-header-icon">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h1 className="admin-page-header-title">Admin Panel</h1>
          </div>
          <p className="admin-page-header-subtitle">Manage user assistant configurations and system settings</p>
        </div>

        <AdminTabNav />

        {tab === TAB_GLOBAL && (
          <AdminGlobalSettingsPage embedded />
        )}

        {tab === TAB_PRICING && (
          <AdminPricingSettingsPage embedded />
        )}

        {tab === TAB_PHONE_POOL && (
          <AdminPhonePoolPage />
        )}

        {tab === TAB_PRIVACY_TERMS && (
          <AdminPrivacyTermsPage embedded />
        )}

        {tab === TAB_GA_ANALYTICS && (
          <AdminGAAnalyticsPage />
        )}

        {tab === 'users' && (
          <>
        {error && (
          <div className="admin-page-error">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="admin-page-success">
            <div className="admin-page-success-inner">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 10-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="admin-page-success-content">
                <p className="admin-page-success-text">{successMessage}</p>
                {deletionStats && (
                  <div className="admin-page-deletion-stats">
                    {Object.entries(deletionStats)
                      .filter(([, value]) => value > 0)
                      .map(([key, value]) => (
                        <div key={key} className="admin-page-deletion-stat-row">
                          <span className="admin-page-deletion-stat-label">{formatStatLabel(key as any)}</span>
                          <span className="admin-page-deletion-stat-value">{value}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setSuccessMessage(null);
                  setDeletionStats(null);
                }}
                className="admin-page-success-dismiss"
                aria-label="Dismiss success message"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div className="admin-page-controls">
          <div className="admin-page-controls-inner">
            <div className="admin-page-controls-row">
              <div className="admin-page-search-wrap">
                <div className="admin-page-search-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="admin-page-search-input"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="admin-page-select"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="not-configured">Not Configured</option>
              </select>
            </div>
          </div>
        </div>

        <div className="admin-page-results">
          <p className="admin-page-results-text">
            Showing <span className="font-semibold text-cyan-400">{filteredUsers.length}</span> of{' '}
            <span className="font-semibold">{users.length}</span> users
          </p>
          <p className="admin-page-results-hint">Click a user card to expand and view configuration</p>
        </div>

        <div className="admin-page-list">
          {paginatedUsers.map((userWithConfig) => (
            <div key={userWithConfig.user.id} className="admin-page-user-card">
              <div className="admin-page-user-card-inner">
                <div
                  className="admin-page-user-card-main"
                  onClick={() => navigate(`/admin/users/${userWithConfig.user.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/admin/users/${userWithConfig.user.id}`)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="admin-page-user-avatar">
                    <span>
                      {userWithConfig.user.name?.charAt(0) ||
                        userWithConfig.user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="admin-page-user-info">
                    <div className="admin-page-user-name-row">
                      <h3 className="admin-page-user-name">{userWithConfig.user.name || 'No Name'}</h3>
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-md ${getStatusColor(userWithConfig)}`}
                      >
                        {getStatusText(userWithConfig)}
                      </span>
                    </div>
                    <p className="admin-page-user-email">{userWithConfig.user.email}</p>
                    <div className="admin-page-user-meta">
                      <span>
                        Credits:{' '}
                        <span className="text-cyan-400 font-medium">
                          {userWithConfig.user.credit_balance ?? 0}
                        </span>
                      </span>
                      <span>
                        Plan:{' '}
                        <span className="text-amber-400/90 font-medium">
                          {userWithConfig.user.subscription_plan_name ||
                            userWithConfig.user.subscription_plan ||
                            'No plan'}
                        </span>
                      </span>
                      {userWithConfig.assistant_config.exists && (
                        <span className="text-green-400">✓ Configured</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="admin-page-user-actions">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal(userWithConfig);
                    }}
                    className="admin-page-user-btn admin-page-user-btn-delete"
                    title="Delete User"
                    aria-label="Delete User"
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/users/${userWithConfig.user.id}`);
                    }}
                    className="admin-page-user-btn admin-page-user-btn-view"
                    title="View Details"
                    aria-label="View Details"
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredUsers.length > 0 && (
          <AdminPagination
            currentPage={Math.min(currentPage, totalPages)}
            totalPages={totalPages}
            totalItems={filteredUsers.length}
            itemsPerPage={itemsPerPage}
            itemLabel="users"
            onPageChange={setCurrentPage}
          />
        )}

        <div className="admin-page-instructions">
          <h4 className="admin-page-instructions-title">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            Admin Instructions
          </h4>
          <ul className="admin-page-instructions-list">
            <li>
              <span className="admin-page-instructions-bullet">•</span>
              <span>
                <strong className="text-white">Default outbound prompt:</strong> Prepended to every run&apos;s Voice Gateway
                system prompt. Click the button above to open the editor page.
              </span>
            </li>
            <li>
              <span className="admin-page-instructions-bullet">•</span>
              <span>
                <strong className="text-white">Outbound Assistant ID:</strong> Voice Gateway agent ID for outbound calls
                (campaigns)
              </span>
            </li>
            <li>
              <span className="admin-page-instructions-bullet">•</span>
              <span>
                <strong className="text-white">Inbound Assistant ID:</strong> Voice Gateway agent ID for inbound calls
                (customer calls back)
              </span>
            </li>
            <li>
              <span className="admin-page-instructions-bullet">•</span>
              <span>
                <strong className="text-white">Phone Number ID:</strong> Gateway phone number for making calls
              </span>
            </li>
            <li>
              <span className="admin-page-instructions-bullet">•</span>
              <span>
                <strong className="text-white">Status:</strong> Active = user can make calls, Inactive = calls
                blocked, Not Configured = needs setup
              </span>
            </li>
          </ul>
        </div>
          </>
        )}

      {deleteTarget && (
        <AdminPageDeleteUserModal
          user={deleteTarget}
          confirmValue={deleteConfirmValue}
          onConfirmValueChange={setDeleteConfirmValue}
          onClose={closeDeleteModal}
          onConfirm={handleConfirmDeleteUser}
          loading={deleteLoading}
          canConfirm={!!canConfirmDeletion}
        />
      )}

      {deleteConfigTarget && (
        <AdminPageDeleteConfigModal
          userName={deleteConfigTarget.userName}
          onClose={closeDeleteConfigModal}
          onConfirm={handleDeleteConfig}
          loading={deleteConfigLoading}
        />
      )}

      {addCreditsTarget && (
        <AdminPageAddCreditsModal
          userName={addCreditsTarget.userName}
          amount={addCreditsAmount}
          onAmountChange={setAddCreditsAmount}
          description={addCreditsDescription}
          onDescriptionChange={setAddCreditsDescription}
          onClose={closeAddCreditsModal}
          onConfirm={handleAddCredits}
          loading={addCreditsLoading}
        />
      )}

      {inboundConfigModal && (
        <AdminPageInboundConfigModal
          userName={inboundConfigModal.userName}
          loading={loadingInboundConfig}
          saving={savingInboundConfig}
          config={inboundConfig}
          form={inboundEditForm}
          setForm={setInboundEditForm}
          onClose={closeInboundConfigModal}
          onSave={handleSaveInboundConfig}
        />
      )}
      </div>
    </div>
  );
};

export default AdminPage;
